import { create } from 'zustand';
import type { SafetySettings, Device, AlarmType } from '../api/types';
import { mockAPI } from '../api/mockApi';

interface SafetyState {
  settings: SafetySettings | null;
  allowedDevices: Device[];
  isLoading: boolean;
  error: string | null;
  
  fetchSettings: () => Promise<void>;
  toggleSecurity: (enabled: boolean) => Promise<boolean>;
  manageSecurityDevices: (deviceIds: string[]) => Promise<boolean>;
  fetchAllowedSecurityDevices: () => Promise<void>;
  clearError: () => void;
}

export const useSafetyStore = create<SafetyState>((set, get) => ({
  settings: null,
  allowedDevices: [],
  isLoading: false,
  error: null,

  fetchSettings: async () => {
    set({ isLoading: true, error: null });
    
    try {
      const result = await mockAPI.getSafetySettings(1, 'security' as AlarmType, false);
      
      set({ 
        settings: result,
        isLoading: false 
      });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to fetch safety settings',
        isLoading: false 
      });
    }
  },

  toggleSecurity: async (enabled: boolean) => {
    try {
      await mockAPI.setIsEnabledForSecurity(1, enabled, false);
      
      const { settings } = get();
      if (settings) {
        set({ 
          settings: { ...settings, isEnabled: enabled }
        });
      }
      
      return true;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to toggle security'
      });
      return false;
    }
  },

  manageSecurityDevices: async (deviceIds: string[]) => {
    try {
      // Mock implementation - in real app would call API
      const { settings, allowedDevices } = get();
      if (settings) {
        const selectedDevices = allowedDevices.filter(d => deviceIds.includes(d.deviceId));
        set({ 
          settings: { 
            ...settings, 
            deviceList: selectedDevices
          }
        });
      }
      
      return true;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to manage security devices'
      });
      return false;
    }
  },

  fetchAllowedSecurityDevices: async () => {
    set({ isLoading: true, error: null });
    
    try {
      const devices = await mockAPI.getAllowedSecurityDevices(1);
      set({ 
        allowedDevices: devices,
        isLoading: false 
      });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to fetch allowed devices',
        isLoading: false 
      });
    }
  },

  clearError: () => {
    set({ error: null });
  }
}));
